import { Delta } from 'quill/core';
const EMBED_PLACEHOLDER = '\uFFFC';
const LINE_CHANGE_WORD_THRESHOLD = 0.5;
function deltaToPlainText(delta) {
    let text = '';
    for (const op of delta.ops) {
        if (typeof op.insert === 'string') {
            text += op.insert;
        }
        else if (op.insert) {
            text += EMBED_PLACEHOLDER;
        }
    }
    return text;
}
function expandToWordBounds(text, changed) {
    let i = 0;
    while (i < text.length) {
        if (changed[i]) {
            const runStart = i;
            while (i < text.length && changed[i])
                i++;
            const runEnd = i;
            let start = runStart;
            while (start > 0 && text[start - 1] !== '\n' && /\S/.test(text[start - 1])) {
                changed[--start] = true;
            }
            let end = runEnd;
            while (end < text.length && text[end] !== '\n' && /\S/.test(text[end])) {
                changed[end++] = true;
            }
            i = end;
        }
        else {
            i++;
        }
    }
}
function applyLineThreshold(text, changed) {
    let lineStart = 0;
    for (let i = 0; i <= text.length; i++) {
        if (i === text.length || text[i] === '\n') {
            const lineEnd = i;
            let totalWords = 0;
            let changedWords = 0;
            let j = lineStart;
            while (j < lineEnd) {
                while (j < lineEnd && /\s/.test(text[j]))
                    j++;
                if (j >= lineEnd)
                    break;
                const wordStart = j;
                while (j < lineEnd && /\S/.test(text[j]))
                    j++;
                totalWords++;
                let hasChange = false;
                for (let k = wordStart; k < j; k++) {
                    if (changed[k]) {
                        hasChange = true;
                        break;
                    }
                }
                if (hasChange)
                    changedWords++;
            }
            if (totalWords > 0 && changedWords / totalWords > LINE_CHANGE_WORD_THRESHOLD) {
                for (let k = lineStart; k < lineEnd; k++) {
                    changed[k] = true;
                }
            }
            lineStart = i + 1;
        }
    }
}
export default function computeWordLevelDiff(oldDelta, newDelta, charDiffDelta) {
    const oldText = deltaToPlainText(oldDelta);
    const newText = deltaToPlainText(newDelta);
    const oldChanged = new Array(oldText.length).fill(false);
    const newChanged = new Array(newText.length).fill(false);
    let oldPos = 0;
    let newPos = 0;
    for (const op of charDiffDelta.ops) {
        if (op.retain != null) {
            if (typeof op.retain === 'number') {
                oldPos += op.retain;
                newPos += op.retain;
            }
            else {
                oldPos += 1;
                newPos += 1;
            }
        }
        else if (op.delete) {
            for (let i = 0; i < op.delete; i++)
                oldChanged[oldPos + i] = true;
            oldPos += op.delete;
        }
        else if (op.insert) {
            const len = typeof op.insert === 'string' ? op.insert.length : 1;
            for (let i = 0; i < len; i++)
                newChanged[newPos + i] = true;
            newPos += len;
        }
    }
    expandToWordBounds(oldText, oldChanged);
    expandToWordBounds(newText, newChanged);
    applyLineThreshold(oldText, oldChanged);
    applyLineThreshold(newText, newChanged);
    const result = new Delta();
    oldPos = 0;
    newPos = 0;
    for (const op of charDiffDelta.ops) {
        if (op.retain != null) {
            if (typeof op.retain === 'number') {
                let i = 0;
                while (i < op.retain) {
                    const isChanged = oldChanged[oldPos + i] || newChanged[newPos + i];
                    let runLen = 0;
                    while (i + runLen < op.retain &&
                        (oldChanged[oldPos + i + runLen] || newChanged[newPos + i + runLen]) === isChanged) {
                        runLen++;
                    }
                    if (isChanged) {
                        result.delete(runLen);
                        const sliced = newDelta.slice(newPos + i, newPos + i + runLen);
                        for (const slicedOp of sliced.ops) {
                            if (slicedOp.insert !== undefined) {
                                result.insert(slicedOp.insert, slicedOp.attributes);
                            }
                        }
                    }
                    else {
                        result.retain(runLen, op.attributes);
                    }
                    i += runLen;
                }
                oldPos += op.retain;
                newPos += op.retain;
            }
            else {
                result.retain(op.retain, op.attributes);
                oldPos += 1;
                newPos += 1;
            }
        }
        else if (op.delete) {
            result.delete(op.delete);
            oldPos += op.delete;
        }
        else if (op.insert) {
            const len = typeof op.insert === 'string' ? op.insert.length : 1;
            const sliced = newDelta.slice(newPos, newPos + len);
            for (const slicedOp of sliced.ops) {
                if (slicedOp.insert !== undefined) {
                    result.insert(slicedOp.insert, slicedOp.attributes);
                }
            }
            newPos += len;
        }
    }
    return result;
}
